import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '..';
import { DocumentationItem } from '@server/documentation';
import { SalesforceItem } from '../../components/parser/ConfigurationItemTypes';
import { SnowflakeConfigurationItem } from '@server/snowflake-documentation';
import { OpenAiDescription } from '../../components/parser/ParserTypes';
import { DocumentationCardContentViewType } from '../../components/documentation/salesforce-documentation-card/types';
import { ConfigurationComment } from '../../components/documentation/salesforce-documentation-card/annotations/comments/types';
import {
  ReadOnlyAutomation,
  ReadOnlyDedupMatching,
  ReadOnlyRollup,
} from '@server/read-only-elements.types';

export type SelectedCardItem =
  | { platform: 'salesforce'; item: SalesforceItem; contentType: DocumentationCardContentViewType }
  | {
      platform: 'snowflake';
      item: SnowflakeConfigurationItem;
      contentType: DocumentationCardContentViewType;
    };

export type CardRelationshipMap = Record<string, DocumentationItem[]>;

export interface DocumentationCardState {
  selectedItem: SelectedCardItem | null;
  history: SelectedCardItem[];

  dependsOn: Record<string, CardRelationshipMap>;
  usedBy: Record<string, CardRelationshipMap>;
  openAiDescriptions: Record<string, OpenAiDescription>;
  sourceCodeByConfigurationId: {
    [crmOrgId: string]: { [configurationId: string]: string };
  };

  // ---- salesforce only data below ----
  commentsByConfigurationId: {
    [crmOrgId: string]: { [configurationId: string]: ConfigurationComment[] };
  };
  sweepElementsLatestDeployments: {
    [sweepElementId: string]: ReadOnlyAutomation | ReadOnlyDedupMatching | ReadOnlyRollup;
  };
}

const initialState: DocumentationCardState = {
  selectedItem: null,
  history: [],

  dependsOn: {},
  usedBy: {},
  openAiDescriptions: {},
  commentsByConfigurationId: {},
  sourceCodeByConfigurationId: {},
  sweepElementsLatestDeployments: {},
};

export const documentationCardSlice = createSlice({
  name: 'documentationCard',
  initialState,
  reducers: {
    setSelectedCardItem: (
      state,
      action: PayloadAction<SelectedCardItem & { clearHistory?: boolean }>,
    ) => {
      const { clearHistory, ...cardItem } = action.payload;
      if (clearHistory) {
        state.history = [];
      } else if (state.selectedItem) {
        state.history.push(state.selectedItem);
      }
      state.selectedItem = cardItem as SelectedCardItem;
    },

    goBackInCardHistory: (state) => {
      const previous = state.history.pop();
      state.selectedItem = previous ?? null;
    },

    closeCard: (state) => {
      Object.assign(state, initialState);
    },

    setCardContentType: (state, action: PayloadAction<DocumentationCardContentViewType>) => {
      if (state.selectedItem) {
        state.selectedItem.contentType = action.payload;
      }
    },

    setCardDependsOn: (
      state,
      action: PayloadAction<{ metadataId: string; items: CardRelationshipMap }>,
    ) => {
      state.dependsOn[action.payload.metadataId] = action.payload.items;
    },

    setCardUsedBy: (
      state,
      action: PayloadAction<{ metadataId: string; items: CardRelationshipMap }>,
    ) => {
      state.usedBy[action.payload.metadataId] = action.payload.items;
    },

    setCardOpenAiDescription: (
      state,
      action: PayloadAction<{ metadataId: string; description: OpenAiDescription }>,
    ) => {
      state.openAiDescriptions[action.payload.metadataId] = action.payload.description;
    },

    addCommentForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        configurationId: string;
        newComment: ConfigurationComment;
      }>,
    ) => {
      const { crmOrgId, configurationId, newComment } = action.payload;
      const oldComments = state.commentsByConfigurationId[crmOrgId];
      const oldConfigurationComments =
        state.commentsByConfigurationId[crmOrgId]?.[configurationId] ?? [];

      state.commentsByConfigurationId[crmOrgId] = {
        ...oldComments,
        [configurationId]: [...oldConfigurationComments, newComment],
      };
    },

    updateCommentForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        configurationId: string;
        newComment: ConfigurationComment;
      }>,
    ) => {
      const { crmOrgId, configurationId, newComment } = action.payload;
      const oldComments = state.commentsByConfigurationId[crmOrgId];
      const oldConfigurationComments: ConfigurationComment[] =
        state.commentsByConfigurationId[crmOrgId]?.[configurationId] ?? [];

      state.commentsByConfigurationId[crmOrgId] = {
        ...oldComments,
        [configurationId]: oldConfigurationComments.map((comment) =>
          comment.id === newComment.id ? newComment : comment,
        ),
      };
    },

    deleteCommentForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        configurationId: string;
        commentId: string;
      }>,
    ) => {
      const { crmOrgId, configurationId, commentId } = action.payload;
      const oldComments = state.commentsByConfigurationId[crmOrgId];
      const oldConfigurationComments: ConfigurationComment[] =
        state.commentsByConfigurationId[crmOrgId]?.[configurationId] ?? [];

      state.commentsByConfigurationId[crmOrgId] = {
        ...oldComments,
        [configurationId]: oldConfigurationComments.filter((comment) => comment.id !== commentId),
      };
    },

    setCommentsForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        configurationId: string;
        comments: ConfigurationComment[];
      }>,
    ) => {
      const { crmOrgId, configurationId, comments } = action.payload;
      const oldComments = state.commentsByConfigurationId[crmOrgId];
      state.commentsByConfigurationId[crmOrgId] = {
        ...oldComments,
        [configurationId]: comments,
      };
    },

    setSourceCodeForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        configurationId: string;
        sourceCode: string;
      }>,
    ) => {
      const { crmOrgId, configurationId, sourceCode } = action.payload;
      const oldConfigs = state.sourceCodeByConfigurationId[crmOrgId];
      state.sourceCodeByConfigurationId[crmOrgId] = {
        ...oldConfigs,
        [configurationId]: sourceCode,
      };
    },

    setLatestDeployment: (
      state,
      action: PayloadAction<{
        elementId: string;
        readOnlyElement: ReadOnlyAutomation | ReadOnlyDedupMatching | ReadOnlyRollup;
      }>,
    ) => {
      const { readOnlyElement, elementId } = action.payload;
      state.sweepElementsLatestDeployments[elementId] = readOnlyElement;
    },
  },
});

export const {
  setSelectedCardItem,
  goBackInCardHistory,
  closeCard,
  setCardContentType,
  setCardDependsOn,
  setCardUsedBy,
  setCardOpenAiDescription,
  addCommentForConfiguration,
  updateCommentForConfiguration,
  deleteCommentForConfiguration,
  setCommentsForConfiguration,
  setSourceCodeForConfiguration,
  setLatestDeployment,
} = documentationCardSlice.actions;

export const selectDocumentationCardItem = (state: RootState) =>
  state[documentationCardSlice.name]?.selectedItem ?? null;

export const selectDocumentationCardHistoryLength = (state: RootState) =>
  state[documentationCardSlice.name]?.history.length ?? 0;

export const selectDocumentationCardHistory = (state: RootState) =>
  state[documentationCardSlice.name]?.history ?? [];

export const selectCardContentType = (state: RootState) =>
  state[documentationCardSlice.name]?.selectedItem?.contentType ??
  DocumentationCardContentViewType.default;

export const selectCardDependsOn = (metadataId: string) => (state: RootState) =>
  state[documentationCardSlice.name]?.dependsOn[metadataId];

export const selectCardUsedBy = (metadataId: string) => (state: RootState) =>
  state[documentationCardSlice.name]?.usedBy[metadataId];

export const selectCardOpenAiDescription = (metadataId: string) => (state: RootState) =>
  state[documentationCardSlice.name]?.openAiDescriptions[metadataId];

export const selectConfigurationComments =
  (crmOrgId: string, configurationId: string) => (state: RootState) =>
    state[documentationCardSlice.name]?.commentsByConfigurationId?.[crmOrgId]?.[configurationId];

export const selectConfigurationSourceCode =
  (crmOrgId: string, configurationId: string) => (state: RootState) =>
    state[documentationCardSlice.name]?.sourceCodeByConfigurationId?.[crmOrgId]?.[configurationId];

export const selectLatestDeployment = (elementId: string) => (state: RootState) =>
  state[documentationCardSlice.name]?.sweepElementsLatestDeployments[elementId];

export default documentationCardSlice.reducer;
